import React, { useState, useEffect, useCallback } from 'react';
import { Button, CircularProgress, Box, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { createPreservicio, getProductosParaPreservicio, getMiCliente, updateMiTelefono } from '../../api/preservicios';
import { UseAlert } from '../../components/newAlert/NewAlert';

interface Producto {
  Pr_Id: number;
  Pr_Nombre: string;
  Un_Nombre: string;
}

interface LineaProducto {
  Pr_Id: number;
  Pr_Nombre: string;
  Cantidad: number;
}

const CreatePreservicio = ({ onClose }: { onClose: () => void }) => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [lineas, setLineas] = useState<LineaProducto[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<number | ''>('');
  const [cantidad, setCantidad] = useState<number>(1);
  const [marca, setMarca] = useState('');
  const [referencia, setReferencia] = useState('');
  const [placa, setPlaca] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [carroDisponible, setCarroDisponible] = useState(false);
  const [ordenServicio, setOrdenServicio] = useState('');
  const [cargoCliente, setCargoCliente] = useState(false);
  const [cargoComercial, setCargoComercial] = useState(false);
  const [telefono, setTelefono] = useState('');
  const [telefonoOriginal, setTelefonoOriginal] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { NewNotification } = UseAlert();

  const fetchProductos = useCallback(async () => {
    const response = await getProductosParaPreservicio();
    if (response.isOk) {
      setProductos(response.data);
    }
  }, []);

  const fetchMiCliente = useCallback(async () => {
    const response = await getMiCliente();
    if (response.isOk) {
      setTelefono(response.data.Cl_Telefono || '');
      setTelefonoOriginal(response.data.Cl_Telefono || '');
    }
  }, []);

  useEffect(() => {
    fetchProductos();
    fetchMiCliente();
  }, [fetchProductos, fetchMiCliente]);

  const handleAgregarLinea = () => {
    if (!productoSeleccionado || cantidad <= 0) return;
    const producto = productos.find((p) => p.Pr_Id === productoSeleccionado);
    if (!producto) return;

    setLineas((prev) => [...prev, { Pr_Id: producto.Pr_Id, Pr_Nombre: `${producto.Pr_Nombre} ${producto.Un_Nombre}`, Cantidad: cantidad }]);
    setProductoSeleccionado('');
    setCantidad(1);
  };

  const handleEliminarLinea = (index: number) => {
    setLineas((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (lineas.length === 0) {
      setError('Debe agregar al menos un producto');
      return;
    }
    if (!telefono.trim()) {
      setError('Confirma un número de WhatsApp para recibir novedades de tu solicitud');
      return;
    }
    setError('');
    setLoading(true);

    // Si el cliente corrigio su telefono, se guarda en su perfil ANTES de crear el preservicio,
    // para que la notificacion de "preservicio nuevo" (y las de cambio de estado despues) ya
    // usen el numero correcto.
    if (telefono.trim() !== telefonoOriginal.trim()) {
      await updateMiTelefono(telefono.trim());
    }

    const payload = {
      Psrv_VehiculoMarca: marca || null,
      Psrv_VehiculoReferencia: referencia || null,
      Psrv_VehiculoPlacaOSerial: placa || null,
      Psrv_Observaciones: observaciones || null,
      Psrv_CarroDisponible: carroDisponible,
      Psrv_OrdenServicio: ordenServicio || null,
      Psrv_CargoCliente: cargoCliente,
      Psrv_CargoComercial: cargoComercial,
      Detalle: lineas.map((l) => ({ Pr_Id: l.Pr_Id, Cantidad: l.Cantidad })),
    };

    await createPreservicio(payload)
      .then((x) => {
        if (!x.isOk) {
          NewNotification(x.message || 'Error', 'error');
          return;
        }
        NewNotification('Preservicio creado exitosamente', 'success');
        onClose();
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box sx={{ marginTop: 1 }}>
      <TextField
        label='Número de WhatsApp para recibir novedades'
        fullWidth
        size='small'
        value={telefono}
        onChange={(e) => setTelefono(e.target.value)}
        helperText='Confirma o corrige tu número -- aquí te avisaremos del estado de tu solicitud'
        sx={{ marginBottom: 2 }}
      />
      <TextField label='Marca (opcional)' fullWidth size='small' value={marca} onChange={(e) => setMarca(e.target.value)} sx={{ marginBottom: 2 }} />
      <TextField label='Referencia (opcional)' fullWidth size='small' value={referencia} onChange={(e) => setReferencia(e.target.value)} sx={{ marginBottom: 2 }} />
      <TextField label='Placa/Serial (opcional)' fullWidth size='small' value={placa} onChange={(e) => setPlaca(e.target.value)} sx={{ marginBottom: 2 }} />
      <TextField label='Observaciones' fullWidth size='small' multiline minRows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} sx={{ marginBottom: 2 }} />

      <TextField label='Orden de Servicio (opcional)' fullWidth size='small' value={ordenServicio} onChange={(e) => setOrdenServicio(e.target.value)} sx={{ marginBottom: 2 }} />

      <FormGroup row sx={{ marginBottom: 3 }}>
        <FormControlLabel control={<Checkbox checked={carroDisponible} onChange={(e) => setCarroDisponible(e.target.checked)} />} label='Carro disponible' />
        <FormControlLabel control={<Checkbox checked={cargoCliente} onChange={(e) => setCargoCliente(e.target.checked)} />} label='Cargo Cliente' />
        <FormControlLabel control={<Checkbox checked={cargoComercial} onChange={(e) => setCargoComercial(e.target.checked)} />} label='Cargo Comercial' />
      </FormGroup>

      <Box display='flex' gap={1} alignItems='center' sx={{ marginBottom: 2 }}>
        <FormControl fullWidth size='small'>
          <InputLabel>Producto</InputLabel>
          <Select value={productoSeleccionado} onChange={(e) => setProductoSeleccionado(e.target.value as number)} label='Producto'>
            {productos.map((p) => (
              <MenuItem key={p.Pr_Id} value={p.Pr_Id}>
                {p.Pr_Nombre} {p.Un_Nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label='Cant.' type='number' size='small' value={cantidad} onChange={(e) => setCantidad(parseInt(e.target.value, 10) || 1)} sx={{ width: 90 }} />
        <IconButton onClick={handleAgregarLinea} title='Agregar producto'>
          <Add color='success' />
        </IconButton>
      </Box>

      {lineas.length > 0 && (
        <Table size='small' sx={{ marginBottom: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell>Cantidad</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.map((l, index) => (
              <TableRow key={index}>
                <TableCell>{l.Pr_Nombre}</TableCell>
                <TableCell>{l.Cantidad}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEliminarLinea(index)} size='small'>
                    <Delete color='error' fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {error && (
        <Box sx={{ color: 'error.main', marginBottom: 2 }} fontSize='0.85rem'>
          {error}
        </Box>
      )}

      <Box display='flex' justifyContent='space-between' mt={3}>
        <Button onClick={handleSubmit} variant='outlined' size='small' disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Enviar solicitud'}
        </Button>
        <Button onClick={onClose} variant='outlined' color='error' size='small'>
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePreservicio;
